import { ComponentId } from 'pages/reporting/reports/rug/types'
import { Section, Sectionable } from 'types'

const OVERVIEW_LOGICAL_KEY = 'overview'
const FULL_COMPONENT_SECTION_KEY = 'full-component'

export interface ReportHeatmapBundlePathsInput {
  kind: 'section' | 'overview'
  componentId: ComponentId
  sectionKey?: string
  descriptorHash: string
}

export interface ParsedReportHeatmapAssetPath {
  kind: 'section' | 'overview'
  componentId: ComponentId
  sectionKey?: string
  descriptorHash: string
  bundlePath: string
  fileName: 'heatmap.png' | 'legend.png' | 'descriptor.json'
}

export function createReportHeatmapSectionKey(sectionName?: string) {
  if (!sectionName) {
    return FULL_COMPONENT_SECTION_KEY
  }

  const slug = sectionName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return slug || FULL_COMPONENT_SECTION_KEY
}

export function buildReportHeatmapBundlePaths({
  kind,
  componentId,
  sectionKey,
  descriptorHash,
}: ReportHeatmapBundlePathsInput) {
  const logicalKey = kind === 'overview' ? OVERVIEW_LOGICAL_KEY : sectionKey
  const root = kind === 'overview' ? 'fullComponentHeatmap' : 'sectionHeatmap'
  const bundlePath = `asset-screenshot/${root}/${componentId}/${logicalKey}/${descriptorHash}`

  return {
    bundlePath,
    heatmapPath: `${bundlePath}/heatmap.png`,
    legendPath: `${bundlePath}/legend.png`,
    descriptorPath: `${bundlePath}/descriptor.json`,
  }
}

export function parseReportHeatmapAssetPath(
  path: string | undefined
): ParsedReportHeatmapAssetPath | undefined {
  if (!path) return undefined

  const match = path.match(
    /^asset-screenshot\/(fullComponentHeatmap|sectionHeatmap)\/([^/]+)\/([^/]+)\/([^/]+)\/(heatmap\.png|legend\.png|descriptor\.json)$/
  )
  if (!match) return undefined

  const [, root, componentIdRaw, logicalKey, descriptorHash, fileNameRaw] =
    match

  const kind = root === 'fullComponentHeatmap' ? 'overview' : 'section'
  const fileName = fileNameRaw as ParsedReportHeatmapAssetPath['fileName']
  const componentId = componentIdRaw as ComponentId
  const bundlePath = path.slice(0, path.length - fileName.length - 1)

  return {
    kind,
    componentId,
    sectionKey: kind === 'section' ? logicalKey : undefined,
    descriptorHash,
    bundlePath,
    fileName,
  }
}

export function getBundleFilePaths(bundle: {
  heatmapPath: string
  legendPath?: string
  descriptorPath?: string
}) {
  return [bundle.heatmapPath, bundle.legendPath, bundle.descriptorPath].filter(
    Boolean
  ) as string[]
}

export function serializeReportHeatmapDescriptor(descriptor: unknown) {
  return JSON.stringify(descriptor, null, 2)
}

export function findSectionByKey(
  sectionable: Sectionable | undefined,
  sectionKey: string | undefined
): Section | undefined {
  if (!sectionable || !sectionKey) return undefined

  return sectionable.sections.find(
    (section) => createReportHeatmapSectionKey(section.name) === sectionKey
  )
}

export function isFullComponentSectionKey(sectionKey: string | undefined) {
  return sectionKey === FULL_COMPONENT_SECTION_KEY
}
